import { lazy, Suspense, useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, MessageCircle, Search, X } from "lucide-react";
import { useLocation } from "wouter";
import { trackMobileNav, trackHeaderCtaClick, trackDesktopNavClick, trackSitewideAnnouncementInteraction } from "@/lib/analytics";
import { ASSISTANT_CONFIG, isEligiblePath } from "@/components/sales-assistant/config";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SitewideAnnouncementBar, { SITEWIDE_ANNOUNCEMENT } from "@/components/SitewideAnnouncementBar";

const SmartSiteSearch = lazy(() => import("@/components/SmartSiteSearch"));

// Logo: correctly sized variants (32x32 at 1x, 64x64 at 2x retina) - saves ~34KB vs 457x457 source
const LOGO_URL = "/media/logo-32_869c351c.webp";
const LOGO_URL_2X = "/media/logo-64_95919a69.webp";

// ── Nav data ──────────────────────────────────────────────────────────────────

const coreServices = [
  { label: "Agentic AI Lead Gen", href: "/services/gemini-agentic-ai/", desc: "AI lead generation systems live in 30 days", accent: "#FF8C00", icon: "🤖" },
  { label: "SEO, AEO & GEO", href: "/services/seo-aeo-geo/", desc: "Evidence-led SEO, AEO and GEO for AI answer systems", accent: "#00D4AA", icon: "🔍" },
  { label: "PPC & Performance Max", href: "/services/ppc-performance-max/", desc: "ROAS-led Google Ads & Performance Max", accent: "#7C3AED", icon: "📈" },
  { label: "AI Marketing Strategy", href: "/services/ai-marketing-strategy/", desc: "GTM systems deployed, not just planned", accent: "#FF8C00", icon: "🎯" },
  { label: "Account-Based Marketing", href: "/services/account-based-marketing/", desc: "Buying committee targeting for complex B2B", accent: "#00D4AA", icon: "🏢" },
];

const specialistServices = [
  { label: "AI Websites", href: "/services/ai-websites/", desc: "AI-powered websites built as growth engines", accent: "#FF8C00", icon: "⚡" },
  { label: "Integrated Marketing", href: "/services/integrated-marketing-communications/", desc: "Five capabilities, one attribution model", accent: "#00D4AA", icon: "🔗" },
  { label: "AI Training for Leaders", href: "/services/ai-training-for-leaders/", desc: "3-session crash course for executives", accent: "#7C3AED", icon: "🎓" },
  { label: "Google AI Mode Optimization", href: "/services/google-ai-mode-optimisation/", desc: "Be easier to find in Google AI Mode answers", accent: "#00D4AA", icon: "✨" },
  { label: "Preferred Sources Strategy", href: "/services/preferred-sources-strategy/", desc: "Source clarity and evidence for AI answer systems", accent: "#FF8C00", icon: "⭐" },
  { label: "AI Citation Service", href: "/services/ai-citation-service/", desc: "Evidence-led readiness for ChatGPT and Gemini", accent: "#00D4AA", icon: "✦" },
  { label: "AI Citation Programme", href: "/services/ai-citation-programme/", desc: "Source-ready pages, evidence and measurement", accent: "#FF8C00", icon: "◌" },
  { label: "AI Marketing for Ecommerce", href: "/services/ai-marketing-agency-for-ecommerce/", desc: "Shopping, search and paid social with accountable measurement", accent: "#00D4AA", icon: "🛒" },
  { label: "AI Governance & Compliance", href: "/services/ai-governance/", desc: "EU AI Act, GDPR & regulated industry guardrails", accent: "#7C3AED", icon: "🛡️" },
];

const localServices = [
  { label: "Local Presence Plan £299/mo", href: "/services/local-presence-plan/" },
  { label: "AI Visibility Plan £599/mo", href: "/services/ai-visibility-plan/" },
  { label: "Full AI Growth Engine £1,299/mo", href: "/services/full-ai-growth-engine/" },
  { label: "AEO Agency London", href: "/services/aeo-agency-london/" },
  { label: "GEO Agency London", href: "/services/geo-agency-london/" },
  { label: "AI Marketing Agency London", href: "/services/ai-marketing-agency-london/" },
];

const profileRestrictedServiceHrefs = new Set([
  "/services/aeo-agency-london/",
  "/services/geo-agency-london/",
  "/services/ai-marketing-agency-london/",
]);

const resourceLinks = [
  { label: "AEO Content Hub", href: "/aeo/", desc: "4 guides to win AI citations & zero-click visibility", accent: "#FF8C00", icon: "💡" },
  { label: "Case Studies", href: "/case-studies/", desc: "Published case studies and commercial context", accent: "#00D4AA", icon: "📊" },
  { label: "Blog", href: "/blog/", desc: "Insights on AI marketing, AEO, PPC & GTM strategy", accent: "#7C3AED", icon: "✍️" },
  { label: "AI Marketing News", href: "/ai-marketing-news/", desc: "Source-qualified AI releases, governance and commercial analysis", accent: "#00D4AA", icon: "📰" },
  { label: "Methodology", href: "/methodology/", desc: "The (Re)Target framework, science + art of marketing", accent: "#FF8C00", icon: "🔬" },
  { label: "AI Tools Hub", href: "/resources/ai-tools/", desc: "Manus, Gemini, Claude & ChatGPT compared for B2B", accent: "#00D4AA", icon: "🛠️" },
  { label: "About Modi", href: "/modi-elnadi/", desc: "Founder, AI marketing strategist & practitioner", accent: "#7C3AED", icon: "👤" },
];

const freeToolLinks = [
  { label: "UTM Campaign Builder", href: "/tools/utm-builder/", desc: "Build, validate & copy UTM-tagged campaign URLs instantly", accent: "#00D4AA", icon: "🔗" },
  { label: "AI Prompt Improver", href: "/tools/ai-prompt-improver/", desc: "Paste a weak prompt and get 3 optimised versions for ChatGPT, Claude & Gemini", accent: "#a78bfa", icon: "✨" },
  { label: "AI Token Calculator", href: "/tools/ai-token-calculator/", desc: "Estimate token counts & API costs across 30+ LLM models", accent: "#38bdf8", icon: "🧮" },
];

const toolsHubLink = {
  label: "Free AI Marketing Tools",
  href: "/tools/",
  desc: "Compare token costs, improve prompts and build campaign URLs",
  accent: "#FF8C00",
  icon: "⚙",
};

// ── Portal Dropdown ───────────────────────────────────────────────────────────
// Renders directly into document.body to escape the header's backdrop-filter
// stacking context. Position is calculated from the trigger's getBoundingClientRect.

interface PortalDropdownProps {
  open: boolean;
  triggerRect: DOMRect | null;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  /** Offset from left edge of trigger. Negative = shift left. */
  offsetX?: number;
  /** Cancel close timer when mouse enters the panel */
  onPanelMouseEnter?: () => void;
  /** Restart close timer when mouse leaves the panel */
  onPanelMouseLeave?: () => void;
}

function PortalDropdown({ open, triggerRect, onClose, children, width = 620, offsetX = 0, onPanelMouseEnter, onPanelMouseLeave }: PortalDropdownProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);
  if (!mounted || !open || !triggerRect) return null;

  // Position: just below the trigger, clamped to viewport
  // 8px gap - hover bridge below spans full panel width so diagonal mouse
  // movement never exits the hover zone before reaching the panel.
  // Bridge height is gap + 12 = 20px to provide a generous safe zone.
  const gap = 8;
  const top = triggerRect.bottom + gap;
  let left = triggerRect.left + offsetX;
  // Clamp right edge to viewport
  if (left + width > window.innerWidth - 16) {
    left = window.innerWidth - width - 16;
  }
  if (left < 16) left = 16;

  const portal = (
    <>
      {/* Invisible backdrop to catch outside clicks - sits below bridge and panel */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 9488 }}
      />
      {/* Hover bridge: transparent strip spanning the full panel width so diagonal
           mouse movement from any part of the trigger to any part of the panel
           never exits the hover zone and never fires the close timer. */}
      <div
        aria-hidden="true"
        onMouseEnter={onPanelMouseEnter}
        onMouseLeave={onPanelMouseLeave}
        style={{
          position: "fixed",
          top: triggerRect.bottom,
          left,
          width,
          height: gap + 12,
          zIndex: 9499,
          background: "transparent",
        }}
      />
      {/* Dropdown panel */}
      <div
        role="dialog"
        onMouseEnter={onPanelMouseEnter}
        onMouseLeave={onPanelMouseLeave}
        style={{
          position: "fixed",
          top,
          left,
          width,
          background: "#141920",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "10px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05)",
          zIndex: 9500,
          overflowY: "auto",
          maxHeight: "calc(100vh - 80px)",
          animation: "dropdownIn 160ms cubic-bezier(0.23,1,0.32,1) both",
        }}
      >
        {children}
      </div>
    </>
  );

  return createPortal(portal, document.body);
}

// ── Services Dropdown Content ─────────────────────────────────────────────────

function ServicesDropdown({ onClose, profileRestrictedServices = false }: { onClose: () => void; profileRestrictedServices?: boolean }) {
  const visibleLocalServices = profileRestrictedServices
    ? localServices.filter((service) => !profileRestrictedServiceHrefs.has(service.href))
    : localServices;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        {/* Core Services */}
        <div style={{ padding: "14px 0", borderRight: "1px solid rgba(255,255,255,0.06)" }}>
          <p style={{ padding: "0 16px 10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#FF8C00", fontWeight: 700 }}>
            Core AI Services
          </p>
          {coreServices.map((s) => (
            <DropdownLink key={s.href} href={s.href} label={s.label} desc={s.desc} accent={s.accent} icon={s.icon} onClose={onClose} />
          ))}
        </div>
        {/* Specialist Services */}
        <div style={{ padding: "14px 0" }}>
          <p style={{ padding: "0 16px 10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#00D4AA", fontWeight: 700 }}>
            Specialist Services
          </p>
          {specialistServices.map((s) => (
            <DropdownLink key={s.href} href={s.href} label={s.label} desc={s.desc} accent={s.accent} icon={s.icon} onClose={onClose} />
          ))}
        </div>
      </div>
      {/* Local services row */}
      <div style={{ padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <p style={{ paddingBottom: "6px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.25)", fontWeight: 600 }}>
          Local Services
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px" }}>
          {visibleLocalServices.map((s) => (
            <a
              key={s.href}
              href={s.href}
              onClick={onClose}
              style={{ fontSize: "12px", color: "rgba(255,255,255,0.55)", textDecoration: "none", transition: "color 150ms" }}
              onMouseEnter={e => (e.currentTarget.style.color = "#FF8C00")}
              onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.55)")}
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>
      {/* Footer row */}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 16px", background: "rgba(255,255,255,0.02)" }}>
        <a
          href="/services/"
          onClick={onClose}
          style={{ fontSize: "12px", color: "#FF8C00", textDecoration: "none", fontWeight: 600 }}
          onMouseEnter={e => (e.currentTarget.style.opacity = "0.8")}
          onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
        >
          View all services →
        </a>
        <a
          href="/case-studies/"
          onClick={onClose}
          style={{ fontSize: "12px", color: "rgba(255,255,255,0.45)", textDecoration: "none", transition: "color 150ms" }}
          onMouseEnter={e => (e.currentTarget.style.color = "#00D4AA")}
          onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.45)")}
        >
          Case Studies ↗
        </a>
      </div>
    </>
  );
}

// ── Resources Dropdown Content ────────────────────────────────────────────────

function ResourcesDropdown({ onClose, homepageCompactNav = false }: { onClose: () => void; homepageCompactNav?: boolean }) {
  return (
    <div>
      {/* Resources & Insights section */}
      <div style={{ padding: "14px 0 6px" }}>
        <p style={{ padding: "0 16px 10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#00D4AA", fontWeight: 700 }}>
          Resources & Insights
        </p>
        {resourceLinks.map((r) => (
          <DropdownLink key={r.href} href={r.href} label={r.label} desc={r.desc} accent={r.accent} icon={r.icon} onClose={onClose} />
        ))}
      </div>
      {!homepageCompactNav && <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "12px 0 6px" }}>
        <p style={{ padding: "0 16px 10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#FF8C00", fontWeight: 700 }}>
          🛠 Free Tools
        </p>
        <DropdownLink key={toolsHubLink.href} {...toolsHubLink} onClose={onClose} />
        {freeToolLinks.map((r) => (
          <DropdownLink key={r.href} href={r.href} label={r.label} desc={r.desc} accent={r.accent} icon={r.icon} onClose={onClose} />
        ))}
      </div>}
    </div>
  );
}


// ── Tools Dropdown Content ────────────────────────────────────────────────────
function ToolsDropdown({ onClose }: { onClose: () => void }) {
  return (
    <div style={{ padding: "10px 0 8px" }}>
      <p style={{ padding: "0 16px 10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#a78bfa", fontWeight: 700 }}>
        ⚙ Free Tools
      </p>
      <DropdownLink key={toolsHubLink.href} {...toolsHubLink} onClose={onClose} />
      {freeToolLinks.map((r) => (
        <DropdownLink key={r.href} href={r.href} label={r.label} desc={r.desc} accent={r.accent} icon={r.icon} onClose={onClose} />
      ))}
    </div>
  );
}
// ── Shared Dropdown Link ──────────────────────────────────────────────────────

function DropdownLink({ href, label, desc, accent, icon, onClose }: {
  href: string;
  label: string;
  desc?: string;
  accent: string;
  icon?: string;
  onClose: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      onClick={() => {
        onClose();
        const section = href.startsWith("/tools/") ? "free_tools" : href.startsWith("/services/") ? "services" : href.startsWith("/resources/") || href.startsWith("/blog") || href.startsWith("/aeo") || href.startsWith("/case-studies") || href.startsWith("/methodology") || href.startsWith("/modi") ? "resources" : "static";
        trackDesktopNavClick({ nav_label: label, nav_href: href, nav_section: section });
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: "10px",
        padding: "9px 14px 9px 12px",
        textDecoration: "none",
        borderLeft: `2px solid ${hovered ? accent : "transparent"}`,
        background: hovered ? "rgba(255,255,255,0.04)" : "transparent",
        transform: hovered ? "translateX(2px)" : "translateX(0)",
        transition: "background 140ms ease-out, border-color 140ms ease-out, transform 140ms cubic-bezier(0.23,1,0.32,1)",
      }}
    >
      {/* Icon badge */}
      {icon && (
        <span
          style={{
            flexShrink: 0,
            width: "26px",
            height: "26px",
            borderRadius: "6px",
            background: hovered ? `${accent}22` : "rgba(255,255,255,0.06)",
            border: `1px solid ${hovered ? `${accent}44` : "rgba(255,255,255,0.08)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            lineHeight: 1,
            transition: "background 140ms, border-color 140ms",
            marginTop: "1px",
          }}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
      {/* Text content */}
      <span style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <span style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "13px",
          fontWeight: 500,
          color: hovered ? "white" : "rgba(255,255,255,0.85)",
          transition: "color 140ms",
        }}>
          {label}
          {/* Arrow - slides in on hover */}
          <span style={{
            opacity: hovered ? 1 : 0,
            transform: hovered ? "translateX(0)" : "translateX(-4px)",
            transition: "opacity 140ms, transform 140ms cubic-bezier(0.23,1,0.32,1)",
            color: accent,
            fontSize: "11px",
            fontWeight: 700,
          }}>→</span>
        </span>
        {desc && (
          <span style={{
            fontSize: "11px",
            color: hovered ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.32)",
            marginTop: "2px",
            transition: "color 140ms",
            lineHeight: 1.4,
          }}>
            {desc}
          </span>
        )}
      </span>
    </a>
  );
}

// ── Mobile Portal ─────────────────────────────────────────────────────────────

interface MobilePortalProps {
  mobileOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onOpenSearch: () => void;
  mobileServicesOpen: boolean;
  onToggleServices: () => void;
  mobileResourcesOpen: boolean;
  onToggleResources: () => void;
  mobileToolsOpen: boolean;
  onToggleTools: () => void;
  profileRestrictedServices?: boolean;
  homepageCompactNav?: boolean;
  announcementOffset?: number;
  scoreCtaLabel?: string;
}

function MobilePortal({
  mobileOpen,
  onOpen,
  onClose,
  onOpenSearch,
  mobileServicesOpen,
  onToggleServices,
  mobileResourcesOpen,
  onToggleResources,
  mobileToolsOpen,
  onToggleTools,
  profileRestrictedServices = false,
  homepageCompactNav = false,
  announcementOffset = 0,
  scoreCtaLabel = "Check my AI visibility",
}: MobilePortalProps) {
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setMounted(true);
    // The full navigation begins at xl; keep the compact menu available across
    // the same sub-xl range so 1024px tablets do not lose route access.
    const check = () => setIsMobile(window.innerWidth < 1280);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (!mounted) return null;
  if (!isMobile) return null;

  const visibleLocalServices = profileRestrictedServices
    ? localServices.filter((service) => !profileRestrictedServiceHrefs.has(service.href))
    : localServices;

  const portal = (
    <>
      {/* Burger button
           Uses CSS env(safe-area-inset-top/right) so the button always clears
           the iPhone status bar, notch, and Dynamic Island on all models.
           env(safe-area-inset-top) = 0 on non-notch devices, 44-59px on notch/DI.
           env(safe-area-inset-right) = 0 in portrait, 44px in landscape on notched iPhones.
           The extra 10px/12px padding gives visual breathing room above/beside the button. */}
      <button
        type="button"
        onClick={() => { onOpen(); trackMobileNav({ action: "open" }); }}
        aria-label="Open navigation menu"
        aria-expanded={mobileOpen}
        className="burger-btn"
        style={{
          position: "fixed",
          top: `calc(env(safe-area-inset-top, 0px) + ${announcementOffset + 10}px)`,
          right: "calc(env(safe-area-inset-right, 0px) + 12px)",
          zIndex: 9000,
          /* WCAG 2.5.5: 56x56px gives comfortable touch target above the 44x44 minimum */
          width: "56px",
          height: "56px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(15,20,25,0.88)",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: "10px",
          cursor: "pointer",
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
          padding: 0,
          boxShadow: "0 2px 16px rgba(0,0,0,0.5)",
          transition: "background 100ms ease-out, transform 100ms ease-out",
        }}
      >
        <span style={{ display: "flex", flexDirection: "column", gap: "5px", pointerEvents: "none" }}>
          <span style={{ display: "block", width: "22px", height: "2px", background: "white", borderRadius: "2px" }} />
          <span style={{ display: "block", width: "22px", height: "2px", background: "white", borderRadius: "2px" }} />
          <span style={{ display: "block", width: "16px", height: "2px", background: "white", borderRadius: "2px" }} />
        </span>
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          style={{ position: "fixed", inset: 0, zIndex: 9001 }}
        >
          <div
            onClick={onClose}
            aria-hidden="true"
            style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)" }}
          />
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: "min(360px, 100vw)",
              background: "#0F1419",
              display: "flex",
              flexDirection: "column",
              boxShadow: "-8px 0 40px rgba(0,0,0,0.6)",
              overflowY: "auto",
              animation: "mobileSlideIn 240ms cubic-bezier(0.23,1,0.32,1) both",
            }}
          >
            {/* Panel header - paddingTop clears iPhone notch / Dynamic Island */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 20px",
              paddingTop: "env(safe-area-inset-top, 0px)",
              minHeight: "64px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              flexShrink: 0,
            }}>
              <a href="/" onClick={onClose} style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
                <img
                  src={LOGO_URL}
                  srcSet={`${LOGO_URL} 1x, ${LOGO_URL_2X} 2x`}
                  alt="Integrated.Social"
                  style={{ width: 28, height: 28 }}
                  width={28}
                  height={28}
                />
                <span style={{ color: "white", fontFamily: "Georgia, serif", fontSize: "16px" }}>
                  Integrated<span style={{ color: "#FF8C00", fontFamily: "system-ui, sans-serif", fontWeight: 700 }}>.Social</span>
                </span>
              </a>
              <button
                type="button"
                onClick={() => { onClose(); trackMobileNav({ action: "close" }); }}
                aria-label="Close menu"
                style={{
                  background: "none",
                  border: "none",
                  color: "rgba(255,255,255,0.7)",
                  cursor: "pointer",
                  padding: "10px",
                  margin: "-10px -10px -10px 0",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable nav */}
            <nav style={{ flex: 1, overflowY: "auto", padding: "8px 20px 0", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)" }} aria-label="Mobile navigation links">
              <button
                type="button"
                onClick={() => { onClose(); onOpenSearch(); }}
                style={{ width: "100%", minHeight: "48px", display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", marginTop: "8px", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "6px", background: "rgba(255,255,255,0.04)", color: "white", cursor: "pointer", textAlign: "left", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              >
                <Search size={18} aria-hidden="true" />
                <span style={{ fontSize: "14px", fontWeight: 600 }}>Search the site</span>
              </button>

              {/* Quick links: 3 core services */}
              <div style={{ padding: "16px 0 8px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <p style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#FF8C00", fontWeight: 600, marginBottom: "10px" }}>Core Services</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {[
                    { href: "/services/gemini-agentic-ai/", label: "Agentic AI Lead Gen", desc: "Autonomous pipeline, live in 30 days", bg: "rgba(255,140,0,0.08)", border: "rgba(255,140,0,0.2)", arrow: "#FF8C00", icon: "🤖" },
                    { href: "/services/seo-aeo-geo/", label: "AEO, GEO & AI Search", desc: "Cited in ChatGPT, Gemini & AI Overviews", bg: "rgba(0,212,170,0.06)", border: "rgba(0,212,170,0.2)", arrow: "#00D4AA", icon: "🔍" },
                    { href: "/services/ppc-performance-max/", label: "PPC & Performance Max", desc: "Higher ROAS, lower CPA, full-funnel", bg: "rgba(124,58,237,0.06)", border: "rgba(124,58,237,0.2)", arrow: "#7C3AED", icon: "📈" },
                  ].map((item) => (
                    <a key={item.href} href={item.href}
                      onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: item.label, href: item.href, section: "core_services" }); }}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "12px 14px", background: item.bg,
                        border: `1px solid ${item.border}`, borderRadius: "6px",
                        textDecoration: "none", touchAction: "manipulation",
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ fontSize: "18px", lineHeight: 1, flexShrink: 0 }}>{item.icon}</span>
                        <div>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>{item.label}</div>
                          <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.45)", marginTop: "2px" }}>{item.desc}</div>
                        </div>
                      </div>
                      <span style={{ color: item.arrow, fontSize: "16px", marginLeft: "8px", flexShrink: 0 }}>→</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Services accordion */}
              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <a href="/services/" onClick={onClose}
                    style={{ flex: 1, padding: "16px 0", color: "white", fontWeight: 600, fontSize: "16px", textDecoration: "none" }}>
                    Services
                  </a>
                  <button type="button" onClick={onToggleServices} aria-label="Toggle services submenu" aria-expanded={mobileServicesOpen}
                    style={{ background: "none", border: "none", color: mobileServicesOpen ? "#FF8C00" : "rgba(255,255,255,0.5)", cursor: "pointer", padding: "12px 10px", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
                    <ChevronDown size={20} style={{ transition: "transform 200ms", transform: mobileServicesOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </button>
                </div>
                {mobileServicesOpen && (
                  <div style={{ paddingBottom: "16px" }}>
                    <p style={{ padding: "4px 8px 8px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#FF8C00", fontWeight: 600 }}>Core AI Services</p>
                    {coreServices.map((s) => (
                      <a key={s.href} href={s.href}
                        onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: s.label, href: s.href, section: "services" }); }}
                        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 12px", borderRadius: "4px", textDecoration: "none", minHeight: "44px" }}>
                        <span style={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>{s.icon}</span>
                        <div>
                          <span style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>{s.label}</span>
                          <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>{s.desc}</span>
                        </div>
                      </a>
                    ))}
                    <p style={{ padding: "16px 8px 8px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#00D4AA", fontWeight: 600 }}>Specialist Services</p>
                    {specialistServices.map((s) => (
                      <a key={s.href} href={s.href}
                        onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: s.label, href: s.href, section: "services" }); }}
                        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 12px", borderRadius: "4px", textDecoration: "none", minHeight: "44px" }}>
                        <span style={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>{s.icon}</span>
                        <div>
                          <span style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>{s.label}</span>
                          <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>{s.desc}</span>
                        </div>
                      </a>
                    ))}
                    <p style={{ padding: "16px 8px 8px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)", fontWeight: 600 }}>Local Services</p>
                    {visibleLocalServices.map((s) => (
                      <a key={s.href} href={s.href}
                        onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: s.label, href: s.href, section: "services" }); }}
                        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", fontSize: "14px", color: "rgba(255,255,255,0.7)", textDecoration: "none" }}>
                        <span style={{ fontSize: "14px" }}>📍</span>
                        {s.label}
                      </a>
                    ))}
                    <a href="/services/"
                      onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: "View all services", href: "/services/", section: "services" }); }}
                      style={{ display: "block", margin: "8px 12px 0", padding: "10px 14px", background: "rgba(255,140,0,0.1)", border: "1px solid rgba(255,140,0,0.25)", borderRadius: "4px", fontSize: "13px", color: "#FF8C00", fontWeight: 600, textDecoration: "none" }}>
                      View all services →
                    </a>
                  </div>
                )}
              </div>

              {!homepageCompactNav && <>
              {/* Tools accordion */}
              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <a href="/tools/" onClick={onClose}
                    style={{ flex: 1, padding: "16px 0", color: "white", fontWeight: 600, fontSize: "16px", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px" }}>
                    ⚙ Free Tools
                    <span style={{ display: "inline-block", padding: "1px 5px", fontSize: "9px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", background: "#F59E0B", color: "#1a1a2e", borderRadius: "3px", lineHeight: "14px" }}>FREE</span>
                  </a>
                  <button type="button" onClick={onToggleTools} aria-label="Toggle tools submenu" aria-expanded={mobileToolsOpen}
                    style={{ background: "none", border: "none", color: mobileToolsOpen ? "#a78bfa" : "rgba(255,255,255,0.5)", cursor: "pointer", padding: "12px 10px", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
                    <ChevronDown size={20} style={{ transition: "transform 200ms", transform: mobileToolsOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </button>
                </div>
                {mobileToolsOpen && (
                  <div style={{ paddingBottom: "16px" }}>
                    <a href={toolsHubLink.href}
                      onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: toolsHubLink.label, href: toolsHubLink.href, section: "free_tools" }); }}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "4px", textDecoration: "none", background: "rgba(255,140,0,0.08)", border: "1px solid rgba(255,140,0,0.18)", margin: "0 12px 8px" }}>
                      <span style={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>{toolsHubLink.icon}</span>
                      <div>
                        <span style={{ display: "block", fontSize: "14px", fontWeight: 600, color: "#FFB347" }}>{toolsHubLink.label}</span>
                        <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.5)", marginTop: "2px" }}>{toolsHubLink.desc}</span>
                      </div>
                    </a>
                    {freeToolLinks.map((r) => (
                      <a key={r.href} href={r.href}
                        onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: r.label, href: r.href, section: "free_tools" }); }}
                        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "4px", textDecoration: "none" }}>
                        <span style={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>{r.icon}</span>
                        <div>
                          <span style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>{r.label}</span>
                          <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>{r.desc}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
              </>}
              {/* Resources accordion */}
              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <a href="/blog/" onClick={onClose}
                    style={{ flex: 1, padding: "16px 0", color: "white", fontWeight: 600, fontSize: "16px", textDecoration: "none" }}>
                    Resources
                  </a>
                  <button type="button" onClick={onToggleResources} aria-label="Toggle resources submenu" aria-expanded={mobileResourcesOpen}
                    style={{ background: "none", border: "none", color: mobileResourcesOpen ? "#00D4AA" : "rgba(255,255,255,0.5)", cursor: "pointer", padding: "12px 10px", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
                    <ChevronDown size={20} style={{ transition: "transform 200ms", transform: mobileResourcesOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </button>
                </div>
                {mobileResourcesOpen && (
                  <div style={{ paddingBottom: "16px" }}>
                    {resourceLinks.map((r) => (
                      <a key={r.href} href={r.href}
                        onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: r.label, href: r.href, section: "resources" }); }}
                        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "4px", textDecoration: "none" }}>
                        <span style={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>{r.icon}</span>
                        <div>
                          <span style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>{r.label}</span>
                          <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>{r.desc}</span>
                        </div>
                      </a>
                    ))}
                    {!homepageCompactNav && <>
                    {/* Free Tools sub-section in mobile */}
                    <div style={{ margin: "8px 12px 4px", paddingTop: "8px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                      <p style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#FF8C00", fontWeight: 700, marginBottom: "6px" }}>🛠 Free Tools</p>
                    </div>
                    <a href={toolsHubLink.href}
                      onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: toolsHubLink.label, href: toolsHubLink.href, section: "free_tools" }); }}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "4px", textDecoration: "none" }}>
                      <span style={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>{toolsHubLink.icon}</span>
                      <div>
                        <span style={{ display: "block", fontSize: "14px", fontWeight: 600, color: "#FFB347" }}>{toolsHubLink.label}</span>
                        <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>{toolsHubLink.desc}</span>
                      </div>
                    </a>
                    {freeToolLinks.map((r) => (
                      <a key={r.href} href={r.href}
                        onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: r.label, href: r.href, section: "free_tools" }); }}
                        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "4px", textDecoration: "none" }}>
                        <span style={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>{r.icon}</span>
                        <div>
                          <span style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>{r.label}</span>
                          <span style={{ display: "block", fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>{r.desc}</span>
                        </div>
                      </a>
                    ))}
                    </>}
                  </div>
                )}
              </div>

              {/* Static links */}
              {[
                { label: "Pricing", href: "/pricing/", icon: "£" },
                { label: "Results", href: "/case-studies/", icon: "📊" },
                { label: "About", href: "/modi-elnadi/", icon: "👤" },
                { label: "Contact", href: "/contact/", icon: "✉️" },
              ].map((item) => (
                <div key={item.href} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <a href={item.href}
                    onClick={() => { onClose(); trackMobileNav({ action: "link_tap", label: item.label, href: item.href, section: "static" }); }}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "16px 0", color: "rgba(255,255,255,0.85)", fontSize: "16px", fontWeight: 500, textDecoration: "none" }}>
                    <span style={{ fontSize: "16px" }}>{item.icon}</span>
                    {item.label}
                  </a>
                </div>
              ))}
            </nav>

            {/* Fixed CTA */}
            <div style={{ flexShrink: 0, padding: "20px", borderTop: "1px solid rgba(255,255,255,0.1)", background: "#0F1419" }}>
              <a href="/free-ai-growth-audit/"
                onClick={() => { onClose(); trackMobileNav({ action: "cta_tap", label: scoreCtaLabel, href: "/free-ai-growth-audit/", section: "cta" }); }}
                style={{ display: "block", textAlign: "center", background: "#FF8C00", color: "#0F1419", padding: "16px 20px", fontSize: "15px", fontWeight: 700, borderRadius: "4px", textDecoration: "none", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
                {scoreCtaLabel}
              </a>
              <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "11px", textAlign: "center", marginTop: "6px" }}>
                Free AI Visibility Score · No card needed
              </p>
              <a href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
                onClick={() => { onClose(); trackMobileNav({ action: "cta_tap", label: "Book a discovery call", href: "https://calendly.com/modielnadi/30min", section: "cta" }); }}
                style={{ display: "block", textAlign: "center", marginTop: "10px", padding: "13px 20px", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "4px", color: "rgba(255,255,255,0.75)", fontSize: "14px", fontWeight: 500, textDecoration: "none", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
                Book a discovery call
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes mobileSlideIn {
          from { transform: translateX(100%); opacity: 0.6; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes mobileSlideIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
        }
        body.mobile-menu-open {
          overflow: hidden;
          position: fixed;
          top: calc(-1 * var(--scroll-y, 0px));
          width: 100%;
        }
      `}</style>
    </>
  );

  return createPortal(portal, document.body);
}

// ── Desktop Nav Button ────────────────────────────────────────────────────────

function NavButton({
  label,
  href,
  accentColor,
  isOpen,
  onToggle,
  onOpen,
  onMouseLeaveDelay,
  onLinkClick,
  badge,
}: {
  label: string;
  href: string;
  accentColor: string;
  isOpen: boolean;
  onToggle: (rect: DOMRect) => void;
  /** Called when the wrapper is hovered - opens the dropdown immediately */
  onOpen: (rect: DOMRect) => void;
  /** Called when the mouse leaves the wrapper - parent handles close-delay */
  onMouseLeaveDelay: () => void;
  onLinkClick: () => void;
  /** Optional small pill badge rendered after the label (e.g. "FREE") */
  badge?: string;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);

  const handleToggle = useCallback(() => {
    if (btnRef.current) {
      onToggle(btnRef.current.getBoundingClientRect());
    }
  }, [onToggle]);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    if (wrapperRef.current) {
      onOpen(wrapperRef.current.getBoundingClientRect());
    }
  }, [onOpen]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    onMouseLeaveDelay();
  }, [onMouseLeaveDelay]);

  const active = isOpen || hovered;

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "1px",
        position: "relative",
        // Bottom accent bar: visible only when dropdown is open (not just hovered)
        paddingBottom: "2px",
      }}
    >
      {/* Active underline bar */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "2px",
          borderRadius: "1px",
          background: accentColor,
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "scaleX(1)" : "scaleX(0.4)",
          transition: "opacity 150ms, transform 150ms cubic-bezier(0.23,1,0.32,1)",
          transformOrigin: "left center",
        }}
      />
      <a
        href={href}
        onClick={onLinkClick}
        style={{
          color: active ? accentColor : "rgba(255,255,255,0.8)",
          fontSize: "14px",
          fontWeight: active ? 600 : 500,
          textDecoration: "none",
          transition: "color 150ms, font-weight 0ms",
          padding: "4px 0",
        }}
      >
        {label}
        {badge && (
          <span
            aria-label={badge}
            style={{
              display: "inline-block",
              marginLeft: "5px",
              padding: "1px 5px",
              fontSize: "9px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              background: "#F59E0B",
              color: "#1a1a2e",
              borderRadius: "3px",
              lineHeight: "14px",
              verticalAlign: "middle",
              position: "relative",
              top: "-1px",
            }}
          >
            {badge}
          </span>
        )}
      </a>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-label={`Open ${label} menu`}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "8px 8px",
          color: active ? accentColor : "rgba(255,255,255,0.5)",
          transition: "color 150ms",
          display: "flex",
          alignItems: "center",
        }}
      >
        <ChevronDown
          size={14}
          style={{ transition: "transform 200ms", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
    </div>
  );
}

// ── Main Header Component ─────────────────────────────────────────────────────

export default function Header({ profileRestrictedServices = false, homepageCompactNav = false }: { profileRestrictedServices?: boolean; homepageCompactNav?: boolean }) {
  const [location] = useLocation();
  const scoreCtaLabel = /^\/local(?:\/|$)/.test(location) ? "AI Visibility" : "Check my AI visibility";
  const [scrolled, setScrolled] = useState(false);
  const [showHeaderScore, setShowHeaderScore] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [servicesTriggerRect, setServicesTriggerRect] = useState<DOMRect | null>(null);
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const [resourcesTriggerRect, setResourcesTriggerRect] = useState<DOMRect | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [toolsTriggerRect, setToolsTriggerRect] = useState<DOMRect | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTriggerRef = useRef<HTMLButtonElement | null>(null);
  const assistantAvailable = ASSISTANT_CONFIG.enabled && isEligiblePath(location);
  const requestAssistant = () => window.dispatchEvent(new Event("isa-open-request"));
  const announcementOffset = announcementVisible ? 44 : 0;
  const handleAnnouncementVisibilityChange = useCallback((visible: boolean) => {
    setAnnouncementVisible(visible);
  }, []);
  const handleAnnouncementCtaClick = useCallback(() => {
    trackSitewideAnnouncementInteraction({
      announcement_id: SITEWIDE_ANNOUNCEMENT.id,
      action: "cta_click",
    });
  }, []);
  const handleAnnouncementDismiss = useCallback(() => {
    trackSitewideAnnouncementInteraction({
      announcement_id: SITEWIDE_ANNOUNCEMENT.id,
      action: "dismiss",
    });
  }, []);

  // Scroll effect
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowHeaderScore(window.scrollY > 600);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      const scrollY = window.scrollY;
      document.documentElement.style.setProperty("--scroll-y", `${scrollY}px`);
      document.body.classList.add("mobile-menu-open");
    } else {
      const scrollY = getComputedStyle(document.documentElement).getPropertyValue("--scroll-y");
      document.body.classList.remove("mobile-menu-open");
      window.scrollTo(0, parseInt(scrollY || "0", 10));
    }
    return () => { document.body.classList.remove("mobile-menu-open"); };
  }, [mobileOpen]);

  // Escape key closes everything
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setServicesOpen(false);
        setResourcesOpen(false);
        setToolsOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Close dropdowns on scroll - only attach listener when a dropdown is open
  useEffect(() => {
    if (!servicesOpen && !resourcesOpen && !toolsOpen) return;
    const onScroll = () => {
      setServicesOpen(false);
      setResourcesOpen(false);
      setToolsOpen(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [servicesOpen, resourcesOpen]);

  // Hover-intent timers - prevent flicker when mouse briefly leaves trigger
  const servicesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resourcesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeAll = useCallback(() => {
    setServicesOpen(false);
    setResourcesOpen(false);
    setToolsOpen(false);
  }, []);

  const openSearch = useCallback((trigger?: HTMLButtonElement) => {
    searchTriggerRef.current = trigger ?? null;
    closeAll();
    setSearchOpen(true);
  }, [closeAll]);

  const handleSearchOpenChange = useCallback((open: boolean) => {
    setSearchOpen(open);
    if (!open) window.setTimeout(() => searchTriggerRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", onShortcut);
    return () => window.removeEventListener("keydown", onShortcut);
  }, [openSearch]);

  const handleServicesToggle = useCallback((rect: DOMRect) => {
    setServicesTriggerRect(rect);
    setServicesOpen(v => !v);
    setResourcesOpen(false);
  }, []);

  const handleResourcesToggle = useCallback((rect: DOMRect) => {
    setResourcesTriggerRect(rect);
    setResourcesOpen(v => !v);
    setServicesOpen(false);
  }, []);

  // Hover open - cancel any pending close timer and open immediately.
  // Only update the triggerRect when the dropdown is currently CLOSED so the
  // panel position does not change (and re-trigger the open animation) while
  // the mouse is already inside the open menu - that was the blink root cause.
  const handleServicesHoverOpen = useCallback((rect: DOMRect) => {
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    if (resourcesCloseTimer.current) clearTimeout(resourcesCloseTimer.current);
    setServicesOpen(prev => {
      if (!prev) setServicesTriggerRect(rect);
      return true;
    });
    setResourcesOpen(false);
    setToolsOpen(false);
  }, []);

  const handleResourcesHoverOpen = useCallback((rect: DOMRect) => {
    if (resourcesCloseTimer.current) clearTimeout(resourcesCloseTimer.current);
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    if (toolsCloseTimer.current) clearTimeout(toolsCloseTimer.current);
    setResourcesOpen(prev => {
      if (!prev) setResourcesTriggerRect(rect);
      return true;
    });
    setServicesOpen(false);
    setToolsOpen(false);
  }, []);

  // Hover leave - close after 800ms delay so mouse can comfortably reach the portal panel
  // even on diagonal paths across a large dropdown (e.g. Services with 620px wide panel).
  const handleServicesMouseLeave = useCallback(() => {
    servicesCloseTimer.current = setTimeout(() => setServicesOpen(false), 800);
  }, []);
  const handleResourcesMouseLeave = useCallback(() => {
    resourcesCloseTimer.current = setTimeout(() => setResourcesOpen(false), 800);
  }, []);


  const toolsCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleToolsToggle = useCallback((rect: DOMRect) => {
    setToolsTriggerRect(rect);
    setToolsOpen(v => !v);
    setServicesOpen(false);
    setResourcesOpen(false);
  }, []);
  const handleToolsHoverOpen = useCallback((rect: DOMRect) => {
    if (toolsCloseTimer.current) clearTimeout(toolsCloseTimer.current);
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    if (resourcesCloseTimer.current) clearTimeout(resourcesCloseTimer.current);
    setToolsOpen(prev => {
      if (!prev) setToolsTriggerRect(rect);
      return true;
    });
    setServicesOpen(false);
    setResourcesOpen(false);
  }, []);
  const handleToolsMouseLeave = useCallback(() => {
    toolsCloseTimer.current = setTimeout(() => setToolsOpen(false), 800);
  }, []);
  const cancelToolsClose = useCallback(() => {
    if (toolsCloseTimer.current) clearTimeout(toolsCloseTimer.current);
  }, []);
  // Cancel close when mouse re-enters the portal panel
  const cancelServicesClose = useCallback(() => {
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
  }, []);

  const cancelResourcesClose = useCallback(() => {
    if (resourcesCloseTimer.current) clearTimeout(resourcesCloseTimer.current);
  }, []);

  return (
    <>
      {/* ── Keyframe definitions ── */}
      <style>{`
        @keyframes dropdownIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Reserves vertical clearance for the optional 44px announcement without
          requiring route-by-route changes to existing page layouts. */}
      <div aria-hidden="true" style={{ height: announcementOffset }} />

      {/* ── Fixed announcement and header stack ── */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }}
      >
        <SitewideAnnouncementBar
          onCtaClick={handleAnnouncementCtaClick}
          onDismiss={handleAnnouncementDismiss}
          onVisibilityChange={handleAnnouncementVisibilityChange}
        />
        <header
          style={{
            transition: "background 300ms, box-shadow 300ms",
            background: scrolled ? "rgba(15,20,25,0.97)" : "rgba(26,35,50,0.92)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.4)" : "none",
            /* Extend header behind iPhone status bar / notch / Dynamic Island. */
            paddingTop: "env(safe-area-inset-top, 0px)",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "64px" }}>

            {/* Logo */}
            <a href="/" style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none", flexShrink: 0 }} aria-label="Integrated.Social Home">
              <img
                src={LOGO_URL}
                srcSet={`${LOGO_URL} 1x, ${LOGO_URL_2X} 2x`}
                alt="Integrated.Social"
                style={{ width: 32, height: 32 }}
                width={32}
                height={32}
                loading="eager"
                fetchPriority="auto"
              />
              <span style={{ color: "white", fontFamily: "Georgia, serif", fontSize: "18px", letterSpacing: "-0.02em" }}>
                Integrated<span style={{ color: "#FF8C00", fontFamily: "system-ui, sans-serif", fontWeight: 700 }}>.Social</span>
              </span>
            </a>

            <div className="xl:hidden" style={{ display: "flex", alignItems: "center", gap: "6px", marginRight: "58px" }}>
              <button type="button" onClick={(event) => openSearch(event.currentTarget)} aria-label="Search the Integrated.Social website" className="hidden min-[430px]:inline-flex" style={{ minWidth: "40px", minHeight: "40px", alignItems: "center", justifyContent: "center", padding: "8px", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "4px", background: "transparent", color: "white", cursor: "pointer" }}><Search size={17} aria-hidden="true" /></button>
              {assistantAvailable && <button type="button" onClick={requestAssistant} aria-label="Ask the Integrated.Social AI assistant" style={{ minWidth: "46px", minHeight: "40px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px", padding: "8px 9px", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "4px", background: "transparent", color: "white", fontSize: "12px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}><MessageCircle size={16} aria-hidden="true" /> AI</button>}
              <a
                href="/free-ai-growth-audit/"
                onClick={() => trackHeaderCtaClick({ cta_label: scoreCtaLabel, source: "mobile" })}
                style={{ padding: "8px 9px", border: "1px solid rgba(255,140,0,0.65)", borderRadius: "4px", color: "#FFB347", fontSize: "11px", fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}
              >
                {scoreCtaLabel}</a>
            </div>

            {/* ── Desktop Nav ── */}
            <nav className="hidden xl:flex items-center gap-5" aria-label="Main navigation">

              <NavButton
                label="Services"
                href="/services/"
                accentColor="#FF8C00"
                isOpen={servicesOpen}
                onToggle={handleServicesToggle}
                onOpen={handleServicesHoverOpen}
                onMouseLeaveDelay={handleServicesMouseLeave}
                onLinkClick={closeAll}
              />

              {!homepageCompactNav && <NavButton
                label="⚙ Tools"
                href="/tools/"
                accentColor="#a78bfa"
                isOpen={toolsOpen}
                onToggle={handleToolsToggle}
                onOpen={handleToolsHoverOpen}
                onMouseLeaveDelay={handleToolsMouseLeave}
                onLinkClick={closeAll}
                badge="FREE"
              />}
              <NavButton
                label="Resources"
                href="/blog/"
                accentColor="#00D4AA"
                isOpen={resourcesOpen}
                onToggle={handleResourcesToggle}
                onOpen={handleResourcesHoverOpen}
                onMouseLeaveDelay={handleResourcesMouseLeave}
                onLinkClick={closeAll}
              />

              <NavLinkItem href="/pricing/" label="Pricing" accentColor="#FF8C00" onClick={closeAll} />

              {/* Results */}
              <NavLinkItem href="/case-studies/" label="Results" accentColor="#FF8C00" onClick={closeAll} />

              {/* About */}
              <NavLinkItem href="/modi-elnadi/" label="About" accentColor="white" onClick={closeAll} />

              {/* Header actions */}
              <button
                type="button"
                onClick={(event) => openSearch(event.currentTarget)}
                aria-label="Search the Integrated.Social website"
                title="Search the site (Ctrl or Command K)"
                style={{ minHeight: "40px", minWidth: "40px", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "10px", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "4px", background: "transparent", color: "white", cursor: "pointer" }}
              >
                <Search size={16} aria-hidden="true" />
              </button>
              {assistantAvailable && <button
                type="button"
                onClick={requestAssistant}
                aria-label="Ask the Integrated.Social AI assistant"
                style={{ minHeight: "40px", display: "inline-flex", alignItems: "center", gap: "7px", padding: "10px 13px", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "4px", background: "transparent", color: "white", fontSize: "13px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                <MessageCircle size={16} aria-hidden="true" /> Ask AI
              </button>}
              {showHeaderScore && location !== "/free-ai-growth-audit/" ? (
                <div className="hidden w-[330px] xl:block"><ScoreInlineForm placement="header" compact /></div>
              ) : <div className="group relative flex">
                <a
                  href="/free-ai-growth-audit/"
                  aria-describedby="free-ai-visibility-score-tooltip"
                  onClick={() => { closeAll(); trackHeaderCtaClick({ cta_label: scoreCtaLabel, source: "desktop" }); }}
                  style={{
                    background: "#FF8C00",
                    color: "#0F1419",
                    padding: "10px 20px",
                    fontSize: "13px",
                    fontWeight: 700,
                    borderRadius: "4px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    transition: "background 150ms",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "#E67E00")}
                  onMouseLeave={e => (e.currentTarget.style.background = "#FF8C00")}
                >
                  {scoreCtaLabel}</a>
                <span
                  id="free-ai-visibility-score-tooltip"
                  role="tooltip"
                  className="pointer-events-none absolute right-0 top-full z-50 mt-2 w-64 translate-y-1 rounded-md border border-white/15 bg-[#0F1419] px-3 py-2 text-xs font-normal leading-5 text-white opacity-0 shadow-[0_12px_32px_rgba(0,0,0,0.45)] transition-[opacity,transform] duration-150 motion-reduce:transition-none group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100"
                >
                  Free 0–100 AI visibility baseline. See how clearly your site is set up for AI answers. No card needed.
                </span>
              </div>}
            </nav>

            </div>
          </div>
        </header>
      </div>

      {/* ── Portal Dropdowns (escape backdrop-filter stacking context) ── */}
      <PortalDropdown
        open={servicesOpen}
        triggerRect={servicesTriggerRect}
        onClose={closeAll}
        width={640}
        offsetX={-16}
        onPanelMouseEnter={cancelServicesClose}
        onPanelMouseLeave={handleServicesMouseLeave}
      >
        <ServicesDropdown onClose={closeAll} profileRestrictedServices={profileRestrictedServices} />
      </PortalDropdown>

      <PortalDropdown
        open={resourcesOpen}
        triggerRect={resourcesTriggerRect}
        onClose={closeAll}
        width={300}
        offsetX={-16}
        onPanelMouseEnter={cancelResourcesClose}
        onPanelMouseLeave={handleResourcesMouseLeave}
      >
        <ResourcesDropdown onClose={closeAll} homepageCompactNav={homepageCompactNav} />
      </PortalDropdown>

      <PortalDropdown
        open={toolsOpen}
        triggerRect={toolsTriggerRect}
        onClose={closeAll}
        width={280}
        offsetX={-16}
        onPanelMouseEnter={cancelToolsClose}
        onPanelMouseLeave={handleToolsMouseLeave}
      >
        <ToolsDropdown onClose={closeAll} />
      </PortalDropdown>
      {/* ── Mobile portal ── */}
      <MobilePortal
        scoreCtaLabel={scoreCtaLabel}
        mobileOpen={mobileOpen}
        onOpen={() => setMobileOpen(true)}
        onClose={() => setMobileOpen(false)}
        onOpenSearch={() => openSearch()}
        mobileServicesOpen={mobileServicesOpen}
        onToggleServices={() => setMobileServicesOpen(v => !v)}
        mobileResourcesOpen={mobileResourcesOpen}
        onToggleResources={() => setMobileResourcesOpen(v => !v)}
        mobileToolsOpen={mobileToolsOpen}
        onToggleTools={() => setMobileToolsOpen(v => !v)}
        profileRestrictedServices={profileRestrictedServices}
        homepageCompactNav={homepageCompactNav}
        announcementOffset={announcementOffset}
      />
      {searchOpen && (
        <Suspense fallback={null}>
          <SmartSiteSearch open={searchOpen} onOpenChange={handleSearchOpenChange} />
        </Suspense>
      )}
    </>
  );
}

// ── Simple nav link with hover colour ────────────────────────────────────────

function NavLinkItem({ href, label, accentColor, onClick }: { href: string; label: string; accentColor: string; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        color: hovered ? accentColor : "rgba(255,255,255,0.8)",
        fontSize: "14px",
        fontWeight: 500,
        textDecoration: "none",
        transition: "color 150ms",
      }}
    >
      {label}
    </a>
  );
}
